import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Image, Upload, Trash2, Copy, Check, Loader2, Search, Grid, List
} from 'lucide-react';
import { toast } from 'sonner';
import { SearchBar, Pagination } from './ListControls';

export function AdminMediaLibrary() {
  const { api } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, [skip, search]);

  useEffect(() => { setSkip(0); }, [search]);

  const fetchFiles = async () => {
    try {
      let url = `/api/admin/files?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await api.get(url);
      setFiles(data.files || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const uploadFiles = e.target.files;
    if (!uploadFiles || uploadFiles.length === 0) return;

    setUploading(true);
    
    for (const file of Array.from(uploadFiles)) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await api.post('/api/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    fetchFiles();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await api.delete(`/api/admin/files/${fileId}`);
      fetchFiles();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const filteredFiles = files; // Search is now server-side

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Media Library</h1>
            <p className="text-[#A0B4CC] mt-1">Manage uploaded images and files</p>
          </div>
          <div className="flex gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary"
              data-testid="upload-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Images
            </Button>
          </div>
        </div>

        {/* Search and view controls */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 max-w-md">
            <SearchBar value={search} onChange={setSearch} placeholder="Search files..." />
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-[#00E5FF] text-[#000B18]' : 'text-[#A0B4CC]'}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-[#00E5FF] text-[#000B18]' : 'text-[#A0B4CC]'}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
          </div>
        ) : files.length === 0 ? (
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="py-12 text-center">
              <Image className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
              <p className="text-[#A0B4CC]">
                {search ? 'No files match your search' : 'No files uploaded yet'}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="mt-4 btn-secondary"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Image
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredFiles.map((file) => (
              <Card key={file.id} className="bg-[#041A3B] border-white/10 overflow-hidden group">
                <div className="aspect-square relative bg-[#000B18]">
                  <img
                    src={`/api/files/${file.id}`}
                    alt={file.original_filename}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23041A3B" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" fill="%235C7599" dy=".3em">No preview</text></svg>'; }}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyUrl(`/api/files/${file.id}`)}
                      className="bg-white/10 text-white hover:bg-white/20"
                    >
                      {copied === `/api/files/${file.id}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteFile(file.id)}
                      className="bg-white/10 text-[#FF1744] hover:bg-white/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <p className="text-white text-xs truncate">{file.original_filename}</p>
                  <p className="text-[#5C7599] text-xs">{formatSize(file.size)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-0 mobile-scroll-wrap">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-[#A0B4CC] font-medium">Preview</th>
                    <th className="text-left p-4 text-[#A0B4CC] font-medium">Filename</th>
                    <th className="text-left p-4 text-[#A0B4CC] font-medium">Size</th>
                    <th className="text-left p-4 text-[#A0B4CC] font-medium">Type</th>
                    <th className="text-right p-4 text-[#A0B4CC] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.map((file) => (
                    <tr key={file.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#000B18]">
                          <img
                            src={`/api/files/${file.id}`}
                            alt={file.original_filename}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-white text-sm">{file.original_filename}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-[#A0B4CC] text-sm">{formatSize(file.size)}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-[#5C7599] text-sm">{file.content_type}</p>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyUrl(`/api/files/${file.id}`)}
                            className="text-[#00E5FF]"
                          >
                            {copied === `/api/files/${file.id}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteFile(file.id)}
                            className="text-[#FF1744]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        <Pagination skip={skip} limit={limit} total={total} onPageChange={setSkip} />
      </div>
    </AdminSidebarLayout>
  );
}
